import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import { bindActionCreators } from 'redux';
import { Select } from 'react-materialize';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import '../styles/map.scss';
import { getThemeClass, getMapThemeColors } from '../utils/themeUtils';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import SatelliteIcon from '@mui/icons-material/Satellite';
import { Checkbox } from 'react-materialize';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AccountInfo from '../lookup/AccountInfo';
import CropFreeIcon from '@mui/icons-material/CropFree';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import ShapeLineOutlinedIcon from '@mui/icons-material/ShapeLineOutlined';
import { TerraDraw, TerraDrawRectangleMode, TerraDrawCircleMode, TerraDrawPolygonMode, TerraDrawLineStringMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';



const containerStyle = {
  width: '100%',
  height: '100%'
};

const mapCenter = { lat: 40.300817, lng: -75.977466 };

const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
const CLUSTER_PIXEL_RADIUS = 20;

const HIDE_POI_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

const generateMapTheme = (baseColor, accentColor, textColor) => [
  { elementType: "geometry", stylers: [{ color: baseColor }] },
  { elementType: "labels.text.stroke", stylers: [{ color: baseColor }] },
  { elementType: "labels.text.fill", stylers: [{ color: textColor }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: accentColor }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: accentColor }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: baseColor }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: textColor }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: accentColor }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: baseColor }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: textColor }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: baseColor }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: accentColor }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: baseColor }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: textColor }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: baseColor }],
  },
  ...HIDE_POI_STYLES,
];

const getClusterThresholdMeters = (zoom, lat) => {
  const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
  return CLUSTER_PIXEL_RADIUS * metersPerPixel;
};

const createPinUrl = (fill, outline, count = null, isSelected = false) => {
  const stroke = isSelected ? '#ffffff' : outline;
  const countLabel = count > 1
    ? `<circle cx="12" cy="9" r="4.5" fill="white" stroke="#333" stroke-width="0.3"/>
       <text x="12" y="9.5" text-anchor="middle" dominant-baseline="middle" font-size="8" font-weight="bold" fill="#000">${count}</text>`
    : '';

  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="${PIN_PATH}" fill="${fill}" stroke="${stroke}" stroke-width="1"/>
      ${countLabel}
    </svg>
  `);
};

const clusterPinItems = (items, thresholdMeters) => {
  if (!items.length) return [];

  const byColor = {};
  items.forEach(item => {
    if (!byColor[item.employeeId]) byColor[item.employeeId] = [];
    byColor[item.employeeId].push(item);
  });

  const clusters = [];
  const canMeasureDistance = window.google?.maps?.geometry?.spherical;

  Object.values(byColor).forEach(colorItems => {
    const parent = colorItems.map((_, i) => i);
    const find = (i) => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const union = (i, j) => {
      parent[find(i)] = find(j);
    };

    if (canMeasureDistance) {
      for (let i = 0; i < colorItems.length; i++) {
        for (let j = i + 1; j < colorItems.length; j++) {
          const dist = window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(colorItems[i].lat, colorItems[i].lng),
            new window.google.maps.LatLng(colorItems[j].lat, colorItems[j].lng)
          );
          if (dist <= thresholdMeters) union(i, j);
        }
      }
    }

    const groups = {};
    colorItems.forEach((item, i) => {
      const root = canMeasureDistance ? find(i) : i;
      if (!groups[root]) groups[root] = [];
      groups[root].push(item);
    });

    Object.values(groups).forEach(group => {
      clusters.push({
        markers: group,
        lat: group.reduce((sum, item) => sum + item.lat, 0) / group.length,
        lng: group.reduce((sum, item) => sum + item.lng, 0) / group.length,
        employeeId: group[0].employeeId,
        count: group.length,
      });
    });
  });

  return clusters;
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const smoothFitBounds = (map, bounds, padding = 80, { duration = 600, onZoomChange, onComplete } = {}) => {
  const startCenter = map.getCenter();
  const startZoom = map.getZoom();
  const startLat = startCenter.lat();
  const startLng = startCenter.lng();
  const startTime = performance.now();

  map.fitBounds(bounds, padding);
  const targetCenter = map.getCenter();
  const targetZoom = map.getZoom();
  const endLat = targetCenter.lat();
  const endLng = targetCenter.lng();

  map.setCenter(startCenter);
  map.setZoom(startZoom);

  let lastReportedZoom = Math.round(startZoom);

  const animate = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    const eased = easeOutCubic(progress);
    const zoom = startZoom + (targetZoom - startZoom) * eased;

    map.setCenter({
      lat: startLat + (endLat - startLat) * eased,
      lng: startLng + (endLng - startLng) * eased,
    });
    map.setZoom(zoom);

    const roundedZoom = Math.round(zoom);
    if (roundedZoom !== lastReportedZoom) {
      lastReportedZoom = roundedZoom;
      onZoomChange?.(roundedZoom);
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      map.setCenter({ lat: endLat, lng: endLng });
      map.setZoom(targetZoom);
      onZoomChange?.(targetZoom);
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
};

const Map = (props) => {
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
  const multiSelectEnabledRef = useRef(multiSelectEnabled);
  
  // Keep ref in sync with state
  useEffect(() => {
    multiSelectEnabledRef.current = multiSelectEnabled;
  }, [multiSelectEnabled]);
  
  const [highlightedMarker, setHighlightedMarker] = useState(null);
  const [isSatelliteMode, setIsSatelliteMode] = useState(false);
  const [activePins, setActivePins] = useState({}); // {employeeID: pinData}
  const [mapMarkers, setMapMarkers] = useState([]);
  const [mapZoom, setMapZoom] = useState(8);
  const [hiddenEmployees, setHiddenEmployees] = useState([]);
  const [displayPinsBy, setDisplayPinsBy] = useState(2); // employee position id, 1 = technician, 2 = manager, 3 = regional manager
  const [mapKey, setMapKey] = useState(0); // Key to force Google Maps reload
  const [adaptiveMarkerSizes, setAdaptiveMarkerSizes] = useState(false); // Toggle for adaptive marker sizing
  const [addressInfoEquipmentExpanded, setAddressInfoEquipmentExpanded] = useState(false);
  // Drawing mode states
  const [drawingMode, setDrawingMode] = useState(null); // 'rectangle', 'circle', 'polygon', 'polyline'
  const [drawnShape, setDrawnShape] = useState(null);
  const [terraDraw, setTerraDraw] = useState(null);
  const [terraDrawAdapter, setTerraDrawAdapter] = useState(null);
  const terraDrawRef = useRef(null);
  const mapRef = useRef(null);
  const isMapAnimatingRef = useRef(false);
  const markerDataRef = useRef([]);
  // In buildMarkers or useEffect, update the ref
  useEffect(() => {
    markerDataRef.current = props.markers;
  }, [props.markers]);

  const [infoPanelDisplayed, setInfoPanelDisplayed] = useState(false);

  useEffect(() => {
    props.showNav(true);

    if (props.authToken) {
      props.fetchMapMarkers(props.authToken, (employees) => {
        assignPins(employees.filter(emp => emp.position_id >= displayPinsBy));
      })
    }
  }, [])

  useEffect(() => {
    setMapMarkers(buildMarkers());
  }, [activePins, displayPinsBy, hiddenEmployees, adaptiveMarkerSizes, selectedMarkers, mapZoom, props.markers, props.employees])

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAycfeEVduEWMYSL2u_ujkAbUywvy3qcSY",
    libraries: ['geometry']
  })

  const [showLegend, setShowLegend] = useState(false)

  const onLoad = React.useCallback(function callback(map) {
    mapRef.current = map;
    map.setZoom(8);
    setMapZoom(map.getZoom());
    
    // Initialize TerraDraw when map is loaded
    if (window.google && window.google.maps) {
      initializeTerraDraw(map);
    }
  }, [])

  const onZoomChanged = useCallback(() => {
    if (isMapAnimatingRef.current || !mapRef.current) {
      return;
    }
    setMapZoom(mapRef.current.getZoom());
  }, []);

  const initializeTerraDraw = (map) => {
    try {
      // Create TerraDraw adapter for Google Maps
      const adapter = new TerraDrawGoogleMapsAdapter({
        lib: window.google.maps,
        map: map,
        coordinatePrecision: 9,
      });
      
      // Create mode instances
      const modes = [
        new TerraDrawSelectMode({
          flags: {
            arbitary: {
              feature: {},
            },
          },
        }),
        new TerraDrawRectangleMode({
          styles: {
            fillColor: '#ff000055',
            outlineColor: '#ff0000',
            outlineWidth: 2,
          },
        }),
        new TerraDrawCircleMode({
          styles: {
            fillColor: '#00ff0055',
            outlineColor: '#00ff00',
            outlineWidth: 2,
          },
        }),
        new TerraDrawPolygonMode({
          styles: {
            fillColor: '#0000ff55',
            outlineColor: '#0000ff',
            outlineWidth: 2,
          },
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: '#ffff00',
            lineStringWidth: 3,
          },
        }),
      ];
      
      // Create TerraDraw instance
      const draw = new TerraDraw({
        adapter: adapter,
        modes: modes,
      });
      
      // Add event listeners for drawing events
      draw.on('change', handleTerraDrawChange);
      draw.on('finish', (id) => {
        const snapshot = draw.getSnapshot();
        handleTerraDrawChange(snapshot);
      });
      
      // Store references
      setTerraDrawAdapter(adapter);
      setTerraDraw(draw);
      terraDrawRef.current = draw;
      
    } catch (error) {
      console.error('Failed to initialize TerraDraw:', error);
    }
  }

  const onUnmount = React.useCallback(function callback(map) {
    // Cleanup if needed
  }, [])

  const colorScheme = props.user?.map_color_scheme || 'ocean';
  const themeClass = getThemeClass(colorScheme);

  const mapThemeColors = useMemo(
    () => getMapThemeColors(colorScheme),
    [colorScheme]
  );

  const toggleSatelliteMode = () => {
    setIsSatelliteMode(prev => !prev);
  };

  const formatAddressString = (marker) => {
    return `${marker.street}, ${marker.city}, ${marker.state} ${marker.zip}`
  }

  const getEmployeeName = useCallback((employeeId) => {
    if (!props.employees) return 'Unknown';
    const employee = props.employees?.find(emp => emp.id === employeeId);
    return employee ? employee.name : 'Unknown';
  }, [props.employees]);

  const handleMarkerClick = (marker) => {
    const currentMultiSelectEnabled = multiSelectEnabledRef.current;

    if (currentMultiSelectEnabled) {
      setSelectedMarkers(prevSelectedMarkers => {
        // Check if marker is already selected using the previous state
        const isAlreadySelected = prevSelectedMarkers.some(selected => selected.id === marker.id);
        if (isAlreadySelected) {
          // Remove the marker if it's already selected
          const filtered = prevSelectedMarkers.filter(selected => selected.id !== marker.id);
          return filtered;
        } else {
          // Add the marker if it's not selected
          const newArray = [...prevSelectedMarkers, marker];
          return newArray;
        }
      });
    } else {
      setSelectedMarkers([marker]);
    }
    setInfoPanelDisplayed(true);
  };

  const handleClusterClick = (cluster, pinUrl) => {
    if (cluster.count === 1) {
      handleMarkerClick({ ...cluster.markers[0].marker, pin: pinUrl });
      return;
    }

    if (multiSelectEnabledRef.current) {
      setSelectedMarkers(prevSelectedMarkers => {
        const clusterMarkers = cluster.markers.map(item => item.marker);
        const allSelected = clusterMarkers.every(marker =>
          prevSelectedMarkers.some(selected => selected.id === marker.id)
        );

        if (allSelected) {
          return prevSelectedMarkers.filter(selected =>
            !clusterMarkers.some(marker => marker.id === selected.id)
          );
        }

        const newMarkers = clusterMarkers.filter(marker =>
          !prevSelectedMarkers.some(selected => selected.id === marker.id)
        );
        return [...prevSelectedMarkers, ...newMarkers];
      });
      setInfoPanelDisplayed(true);
      return;
    }

    if (mapRef.current && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      cluster.markers.forEach(item => bounds.extend({ lat: item.lat, lng: item.lng }));
      isMapAnimatingRef.current = true;
      smoothFitBounds(mapRef.current, bounds, 80, {
        duration: 600,
        onZoomChange: setMapZoom,
        onComplete: () => {
          isMapAnimatingRef.current = false;
          if (mapRef.current) {
            setMapZoom(mapRef.current.getZoom());
          }
        },
      });
    }
  };

  const currentThemeStyles = useMemo(() => generateMapTheme(
    mapThemeColors.base,
    mapThemeColors.accent,
    mapThemeColors.text
  ), [mapThemeColors]);

  const mapOptions = {
    styles: isSatelliteMode ? HIDE_POI_STYLES : currentThemeStyles,
    backgroundColor: mapThemeColors.background,
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
    mapTypeId: isSatelliteMode ? 'satellite' : 'roadmap',
    draggable: true, // Always allow dragging for TerraDraw
    scrollwheel: true, // Always allow scroll for TerraDraw
    disableDoubleClickZoom: false // Allow double-click zoom for TerraDraw
  };

  const blankPin = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg class="blank-pin" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#00000000" stroke="#000000" stroke-width="1"/>
    </svg>
  `)
// green= #5bffad
// yellow = #ffda6b
//red = #ff6c65
// blue = #00a2ff
  const assignPins = (employeeList) => {
    // const pinColors = ['#ff6c65', '#5bffad','#ffda6b', '#00a2ff',];
    const pinColors = ['#ffffff', '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'];
    const outlineColors = ['#000000', '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080', '#000000'];
    console.log('assigning pins', employeeList)

    const pins = {};
    employeeList?.forEach((employee, index) => {
      const fill = pinColors[(index+1) % pinColors.length];
      const outline = outlineColors[Math.floor(index/outlineColors.length)];
      pins[employee.id] = {
        id: employee.id,
        color: fill,
        outline,
        url: createPinUrl(fill, outline),
      };
    });

    console.log('assigned pins', pins)
    setActivePins(pins);
  }

  const buildMarkers = () => {
    if (props.markers?.length < 1 || !window.google?.maps) {
      return [];
    }

    const pinItems = [];

    props.markers?.forEach(marker => {
      if (marker.latitude === null || marker.longitude === null) {
        return;
      }

      let employees = [];

      if (displayPinsBy === 2) {
        employees = [marker.manager];
      } else if (displayPinsBy === 1) {
        employees = marker.technicians;
      } else if (displayPinsBy === 3) {
        employees = [props.employees?.find(emp => emp.id === marker.manager?.managed_by)];
      }

      employees.forEach(employee => {
        if (hiddenEmployees.includes(employee?.id)) {
          return;
        }

        const pinData = activePins?.[employee?.id];
        if (!pinData) {
          return;
        }

        const contractAmount = marker.contract?.amount || 0;
        let size;
        if (adaptiveMarkerSizes) {
          const normalizedAmount = Math.sqrt(contractAmount / 60000);
          size = Math.min(16 + normalizedAmount * 48, 64);
        } else {
          size = 32;
        }

        pinItems.push({
          marker,
          employee,
          employeeId: employee.id,
          lat: marker.latitude,
          lng: marker.longitude,
          size,
          url: pinData.url,
          fill: pinData.color,
          outline: pinData.outline,
        });
      });
    });

    const mapLat = mapRef.current?.getCenter()?.lat() ?? mapCenter.lat;
    const thresholdMeters = getClusterThresholdMeters(mapZoom, mapLat);
    const clusters = clusterPinItems(pinItems, thresholdMeters);

    return clusters.map((cluster, index) => {
      const isCluster = cluster.count > 1;
      const isSelected = cluster.markers.some(item =>
        selectedMarkers.some(selected => selected.id === item.marker.id)
      );
      const pinData = activePins[cluster.employeeId];
      const baseSize = Math.max(...cluster.markers.map(item => item.size));
      const size = isCluster ? Math.min(baseSize + 6, 72) : baseSize;
      const url = isCluster
        ? createPinUrl(pinData.color, pinData.outline, cluster.count, isSelected)
        : createPinUrl(pinData.color, pinData.outline, null, isSelected);

      return (
        <Marker
          key={`cluster-${cluster.employeeId}-${index}-${cluster.count}-${isSelected ? 'selected' : 'unselected'}`}
          position={{ lat: cluster.lat, lng: cluster.lng }}
          onClick={() => handleClusterClick(cluster, url)}
          icon={{
            url,
            scaledSize: new window.google.maps.Size(size, size),
            anchor: new window.google.maps.Point(size / 2, size)
          }}
        />
      );
    });
  }

  const togglePin = (employeeId) => {
    console.log('toggling pin', employeeId, hiddenEmployees)
    setHiddenEmployees(prev => {
      const newHiddenEmployees = [...prev];
      newHiddenEmployees.includes(employeeId) ? newHiddenEmployees.splice(newHiddenEmployees.indexOf(employeeId), 1) : newHiddenEmployees.push(employeeId);
      console.log('newHiddenEmployees', newHiddenEmployees)
      return newHiddenEmployees;
    });
  }

  const toggleVisibilityByPosition = (positionId, show) => {
    let newActiveEmployees = [...Object.keys(activePins)];
    let targetGroup = props.employees?.filter(emp => positionId > 1 ? emp.position_id >= positionId : emp.position_id === positionId).map(emp => emp.id);
    newActiveEmployees = newActiveEmployees.filter(id => !targetGroup.includes(parseInt(id)));
    if (show) {
      newActiveEmployees = newActiveEmployees.concat(targetGroup);
    }
    assignPins(newActiveEmployees.map(id => props.employees?.find(emp => emp.id === parseInt(id))));
  }

  const startDrawing = (mode) => {
    if (!terraDrawRef.current) {
      return;
    }
    
    try {
      // Clear any existing shapes first
      if (drawnShape) {
        clearShape();
      }
      
      // Check if TerraDraw is already enabled
      const isEnabled = terraDrawRef.current.enabled;
      
      // Start TerraDraw if not already started
      if (!isEnabled) {
        terraDrawRef.current.start();
        setTimeout(() => {
          terraDrawRef.current.setMode(mode);
          setDrawingMode(mode);
        }, 100);
      } else {
        terraDrawRef.current.setMode(mode);
        setDrawingMode(mode);
      }
    } catch (error) {
      console.error('Failed to start drawing:', error);
    }
  }

  const stopDrawing = () => {
    if (!terraDrawRef.current) {
      return;
    }
    
    try {
      if (terraDrawRef.current.enabled) {
        terraDrawRef.current.setMode('select');
      }
      setDrawingMode(null);
    } catch (error) {
      console.error('Failed to stop drawing:', error);
    }
  }

  const handleTerraDrawChange = (features) => {
    if (!features || features.length === 0) {
      return;
    }
    
    // Get the latest feature (most recently drawn)
    const latestFeature = features[features.length - 1];
    
    if (!latestFeature) {
      return;
    }
    
    // Try different ways to access geometry
    let geometry = latestFeature.geometry;
    if (!geometry && latestFeature.coordinates) {
      geometry = latestFeature;
    }
    
    if (!geometry || !geometry.type) {
      return;
    }
    
    // Convert TerraDraw feature to our shape format
    const shapeData = {
      id: latestFeature.id || Date.now(),
      type: geometry.type,
      feature: latestFeature,
      coordinates: geometry.coordinates
    };

    console.log('drawn shape', shapeData)
    const pins = pinsWithinShape(shapeData);
    console.log('pins within shape', pins, shapeData, markerDataRef.current)
    setMultiSelectEnabled(true);
    multiSelectEnabledRef.current = true;
    setSelectedMarkers(pins);
    
    // Set the new shape (replacing any previous one)
    setDrawnShape(shapeData);
    
    // Switch to select mode after completing a shape
    if (terraDrawRef.current && terraDrawRef.current.enabled) {
      terraDrawRef.current.setMode('select');
      setDrawingMode(null);
    }
  }

  const pinsWithinShape = (shape) => {
    const coords = shape.coordinates[0] || [];
    const minLat = Math.min(...coords.map(coord => coord[1]));
    const maxLat = Math.max(...coords.map(coord => coord[1]));
    const minLng = Math.min(...coords.map(coord => coord[0]));
    const maxLng = Math.max(...coords.map(coord => coord[0]));
    console.log('searching pins within coords', coords, minLat, maxLat, minLng, maxLng)
    return markerDataRef.current?.filter(marker => {
      return marker.latitude >= minLat && marker.latitude <= maxLat && marker.longitude >= minLng && marker.longitude <= maxLng;
    });
  }

  const clearShape = () => {
    if (terraDrawRef.current && drawnShape) {
      try {
        terraDrawRef.current.removeFeatures([drawnShape.id]);
      } catch (error) {
        console.error('Failed to remove shape:', error);
      }
    }
    setDrawnShape(null);
    setSelectedMarkers([]);
    setInfoPanelDisplayed(false);
    
    if (terraDrawRef.current && terraDrawRef.current.enabled) {
      terraDrawRef.current.setMode('select');
      setDrawingMode(null);
    }
  }

  const contractExpirationDate = (contract) => {
    const termLength = contract?.term_length?.months
    const enddate = contract?.startdate
    const expirationDate = new Date(enddate);
    expirationDate.setMonth(expirationDate.getMonth() + termLength);
    return expirationDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
  }

  const reassignAccountManager = (employeeId) => {
    props.reassignAccountManager(props.authToken, selectedMarkers.map(marker => marker.customer_ids_at_location).flat(), parseInt(employeeId), () => {
      setInfoPanelDisplayed(false);
    });
  }

  return isLoaded ? (
    <div className={`map-container wave-map-page ${themeClass}`}>
      <GoogleMap
        key={mapKey}
        mapContainerStyle={containerStyle}
        zoom={5}
        center={mapCenter}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={onZoomChanged}
        options={mapOptions}
      >
        {mapMarkers}
      </GoogleMap>

      <div className='map-legend-toggle'>
        <div className="map-panel button" onClick={() => setShowLegend(!showLegend)}>
          <LocationOnIcon className='shadow' style={{color: 'white', fontSize: '32px'}} />
          <span className='shadow' style={{color: 'white', fontSize: '16px'}}>Employees</span>
        </div>
      </div>

      <div className={`map-legend-container ${showLegend ? 'open' : ''}`}>
        <div className='map-panel nopadh'>
          <div className="legend-content">
            
            {props?.employees?.sort((a, b) => b.position_id - a.position_id).map((employee, index, array) => (
              <>
                {employee.id === array.find(emp => emp.position_id === 5).id && (
                  <div className="position-divider">
                    <button className="show-group-btn" onClick={() => toggleVisibilityByPosition(2, true)}> Show </button>
                    <span className='shadow'>Managers</span>
                    <button className="hide-group-btn" onClick={() => toggleVisibilityByPosition(2, false)}> Hide </button>
                  </div>
                )}

                {employee.id === array.find(emp => emp.position_id === 1).id && (
                  <div className="position-divider">
                  <button className="show-group-btn" onClick={() => toggleVisibilityByPosition(1, true)}> Show </button>
                  <span className='shadow'>Technicians</span>
                  <button className="hide-group-btn" onClick={() => toggleVisibilityByPosition(1, false)}> Hide </button>
                </div>
                )}

                <div className="legend-row" key={employee.id}>
                  <img 
                    src={hiddenEmployees.includes(employee.id) ? blankPin : (activePins?.[employee?.id]?.url || blankPin)} 
                    className='glow' alt={employee.name} 
                    style={{width: '32px', height: '32px'}}
                    onClick={() => togglePin(employee.id)}
                  />
                  <span className='shadow' style={{color: 'white', fontSize: '16px'}}>{employee.name}</span>
                </div>
              </>
            ))}
          </div>
        </div>
      </div>

      <div className='map-panel pin-display-options vertical'>
        <span style={{color: 'white', fontSize: '16px'}}>Display by:</span>
        <div style={{display: 'flex', flexDirection: 'row', gap: '5px'}}>
          <button className={displayPinsBy === 3 ? 'selected' : ''} onClick={() => {
            setDisplayPinsBy(3);
            assignPins(props.employees.filter(emp => emp.position_id > 3));
          }}>Regional Manager</button>
          <button className={displayPinsBy === 2 ? 'selected' : ''} onClick={() => {
            setDisplayPinsBy(2);
            assignPins(props.employees.filter(emp => emp.position_id > 1));
          }}>Account Manager</button>
          <button className={displayPinsBy === 1 ? 'selected' : ''} onClick={() => {
            setDisplayPinsBy(1);
            assignPins(props.employees.filter(emp => emp.position_id > 0));
          }}>Technicians</button>
        </div>
      </div>

      <div className='map-panel adaptive-sizing-options'>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <Checkbox
            id="adaptive-sizing"
            checked={adaptiveMarkerSizes}
            onChange={(e) => setAdaptiveMarkerSizes(e.target.checked)}
            style={{color: 'white'}}
          />
          <label htmlFor="adaptive-sizing" style={{color: 'white', fontSize: '16px', cursor: 'pointer'}}>
            Adaptive Marker Sizes
          </label>
        </div>
      </div>


      <div className='satellite-toggle-container'>
        <div className='map-panel button' onClick={toggleSatelliteMode}>
          <SatelliteIcon style={{color: 'white', fontSize: '24px'}} />
          <span style={{color: 'white', fontSize: '16px'}}>{isSatelliteMode ? 'Satellite' : 'Map'}</span>
        </div>
      </div>

      <div className='drawing-controls-container'>
        <div className='map-panel button' onClick={() => drawingMode ? stopDrawing() : startDrawing('rectangle')}>
          <CropFreeIcon style={{color: 'white', fontSize: '24px'}} />
          <span style={{color: 'white', fontSize: '16px'}}>{drawingMode ? 'Exit Drawing' : 'Draw Shape'}</span>
        </div>
      </div>

      <div className='multiselect-toggle-container'>
        <div className='map-panel button' onClick={() => setMultiSelectEnabled(!multiSelectEnabled)}>
          {multiSelectEnabled ? (
            <CheckBoxIcon style={{color: 'white', fontSize: '24px'}} />
          ) : (
            <CropSquareIcon style={{color: 'white', fontSize: '24px'}} />
          )}
          <span style={{color: 'white', fontSize: '16px'}}>
            {multiSelectEnabled ? 'Multi-Select On' : 'Multi-Select Off'}
          </span>
          {/* <span>{JSON.stringify(selectedMarkers.map(marker => marker.id))}</span> */}
        </div>
      </div>

      {infoPanelDisplayed && !multiSelectEnabled && (
        <div className='map-panel address-info-container' onClick={e => e.stopPropagation()}>
          <div className="close-address-info-button" onClick={() => setInfoPanelDisplayed(false)}>
            <CloseIcon style={{color: 'white', fontSize: '24px'}} />
          </div>
          <div className='address-info-content'>
            {selectedMarkers[0].customer_ids_at_location.map(id => (
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px', marginBottom: '15px'}}>
                <img className='pin' src={selectedMarkers[0].pin} alt="Marker" style={{width: '32px', height: '32px'}} onClick={() => console.log(selectedMarkers)}/>
                <a target='_blank' rel='noopener noreferrer' href={`/lookup?type=a&id=${id}`} className='customer'>{selectedMarkers[0].customer}</a>
              </div>
            ))}

            <span className='address'>
              {formatAddressString(selectedMarkers[0])}
              <a 
                target='_blank' 
                rel='noopener noreferrer'
                href={`https://www.google.com/maps/search/?api=1&query=${formatAddressString(selectedMarkers[0])}`} 
                className="maps-link"
              >
                Open in Maps
              </a>
            </span>
            
            <div className='account-info'>
              <div className='info-item'>
                Region: &nbsp;
                {props.user.admin ? (
                  <Select options={{classes: 'employee-select'}} value={selectedMarkers[0].manager.managed_by}>
                    {props.employees.filter(emp => emp.position_id > 3).map(emp => ({label: emp.name, value: emp.id})).map(emp => (
                      <option key={emp.value} value={emp.value}>{emp.label}</option>
                    ))}
                  </Select>
                ) : 
                  <span>{props.employees?.find(emp => emp.id === selectedMarkers[0].manager)?.name}</span>
                }
              </div>

              <div className='info-item' style={{marginBottom: 15}}>
                Manager: &nbsp;
                {props.user.admin ? (
                  <Select options={{classes: 'employee-select'}} value={selectedMarkers[0].manager.id} onChange={e => reassignAccountManager(e.target.value)}>
                    {props.employees.filter(emp => emp.position_id > 1).map(emp => ({label: emp.name, value: emp.id})).map(emp => (
                      <option key={emp.value} value={emp.value}>{emp.label}</option>
                    ))}
                  </Select>
                ) : 
                  <span>{selectedMarkers[0].manager.name}</span>
                }
              </div>

              <div className='info-item'>
                Contract amount: ${selectedMarkers[0].contract?.amount?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'} &nbsp;
                <a href={"https://www.proasysinc.com/contracts/show?id=" + selectedMarkers[0].contract?.hash_id} target='_blank' rel='noopener noreferrer'>View Contract</a>
              </div>

              <div className='info-item'>
                Valid until: {contractExpirationDate(selectedMarkers[0].contract)} &nbsp;
              </div>
            </div>

            <span className='system-count'>
              {selectedMarkers[0].equipment.length} systems on site: 
              <button className="expand" onClick={() => setAddressInfoEquipmentExpanded(!addressInfoEquipmentExpanded)}>
                {addressInfoEquipmentExpanded ? 'Collapse' : 'Expand'}
              </button>
            </span>
            {addressInfoEquipmentExpanded && (
              <div className='equipment-container'>
                {selectedMarkers[0].equipment.map((equipment, index) => (
                  <div key={equipment.id || index} className='equipment-item'>
                    <span className='equipment-name'>{equipment.name}</span>
                    <span className='equipment-type'>{equipment.type}</span>
                    <div style={{display: 'flex', flexDirection: 'row', gap: '5px'}}>Technician: &nbsp;{props.user.admin ? (
                      <Select options={{classes: 'employee-select'}} value={equipment.technician.id}>
                        {props.employees.map(emp => ({label: emp.name, value: emp.id})).map(emp => (
                          <option key={emp.value} value={emp.value}>{emp.label}</option>
                        ))}
                      </Select>
                    ) : (
                      <span className='technician'>{equipment.technician.name}</span>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* <div style={{width: '100%'}}>{JSON.stringify(selectedMarkers[0])}</div> */}
          </div>
        </div>
      )}

      {infoPanelDisplayed && multiSelectEnabled && (
        <div className="selected-markers-info-container">
          <span>{selectedMarkers.length} markers selected</span>
          <span>total value of accounts:</span>
          <span>managers of accounts:</span>
          <span>technicians of accounts:</span>
          <span>regional managers</span>
        </div>
      )}
    </div>
  ) : <div>loading...</div>
}

function mapStateToProps(state, props) {
  return {
    user: state.user,
    authToken: state.authToken,
    markers: state.markers,
    employees: state.employees
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Map);